import React, { useState, useEffect, useCallback } from 'react';
import { FiFilter, FiDownload, FiRefreshCw, FiUsers, FiMapPin, FiBriefcase } from 'react-icons/fi';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../auth/AuthProviderUnified';
import ComplianceReportsViewer from '../components/ComplianceReportsViewer';
import logger from '../utils/logger';
import api from '../services/api/client';
import { withQuery } from '../services/api/query';

/**
 * Dedicated Reports Page with Advanced Filtering
 *
 * Allows GCs to:
 * - Filter by subcontractor (SC)
 * - Filter by jobsite
 * - Filter by individual worker
 * - Export reports as PDF/CSV
 * - View all interval reports (daily, weekly, monthly, quarterly, etc.)
 */
export default function Reports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter states
  const [selectedSubcontractor, setSelectedSubcontractor] = useState(
    searchParams.get('subcontractor') || 'all'
  );
  const [selectedJobsite, setSelectedJobsite] = useState(searchParams.get('jobsite') || 'all');
  const [selectedWorker, setSelectedWorker] = useState(searchParams.get('worker') || 'all');
  const [reportInterval, setReportInterval] = useState(searchParams.get('interval') || 'weekly');

  // Data states
  const [subcontractors, setSubcontractors] = useState([]);
  const [jobsites, setJobsites] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check user access
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Only GC admins, SC admins, and admins can access
    const allowedRoles = ['gcAdmin', 'scAdmin', 'admin'];
    if (!allowedRoles.includes(user.role)) {
      navigate('/');
    }
  }, [user, navigate]);

  // Fetch filter options
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('vh_auth_token');

        // Fetch subcontractors (for GCs only)
        if (user.role === 'gcAdmin') {
          try {
            const scData = await api.get('/api/organizations/subcontractors');
            setSubcontractors(scData.subcontractors || []);
          } catch (err) {
            logger.error('Error fetching subcontractors:', err);
          }
        }

        // Fetch jobsites
        try {
          const jobsitesData = await api.get('/api/jobsites');
          setJobsites(jobsitesData.jobsites || jobsitesData || []);
        } catch (err) {
          logger.error('Error fetching jobsites:', err);
        }

        // Fetch workers (filtered by SC if selected)
        const workersParams = {
          role: 'worker',
          ...(selectedSubcontractor && selectedSubcontractor !== 'all' && { organization: selectedSubcontractor }),
        };
        try {
          const workersData = await api.get(withQuery('/api/users', workersParams));
          setWorkers(workersData.users || workersData || []);
        } catch (err) {
          logger.error('Error fetching workers:', err);
        }

        setLoading(false);
      } catch (err) {
        logger.error('Error fetching filter options:', { error: err });
        setError(err.message);
        setLoading(false);
      }
    };

    if (user) {
      fetchFilterOptions();
    }
  }, [user, selectedSubcontractor]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedSubcontractor !== 'all') params.set('subcontractor', selectedSubcontractor);
    if (selectedJobsite !== 'all') params.set('jobsite', selectedJobsite);
    if (selectedWorker !== 'all') params.set('worker', selectedWorker);
    if (reportInterval !== 'weekly') params.set('interval', reportInterval);
    setSearchParams(params);
  }, [selectedSubcontractor, selectedJobsite, selectedWorker, reportInterval, setSearchParams]);

  // Export handlers
  const handleExportPDF = useCallback(async () => {
    try {
      const token = localStorage.getItem('vh_auth_token');
      const queryParams = new URLSearchParams({
        interval: reportInterval,
        format: 'pdf',
        subcontractor: selectedSubcontractor,
        jobsite: selectedJobsite,
        worker: selectedWorker,
      });

      const response = await fetch(`/api/compliance-reports/export?${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `compliance-report-${reportInterval}-${Date.now()}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      logger.error('Error exporting PDF:', { error: err });
      alert('Failed to export PDF. Please try again.');
    }
  }, [reportInterval, selectedSubcontractor, selectedJobsite, selectedWorker]);

  const handleExportCSV = useCallback(async () => {
    try {
      const token = localStorage.getItem('vh_auth_token');
      const queryParams = new URLSearchParams({
        interval: reportInterval,
        format: 'csv',
        subcontractor: selectedSubcontractor,
        jobsite: selectedJobsite,
        worker: selectedWorker,
      });

      const response = await fetch(`/api/compliance-reports/export?${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `compliance-report-${reportInterval}-${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      logger.error('Error exporting CSV:', { error: err });
      alert('Failed to export CSV. Please try again.');
    }
  }, [reportInterval, selectedSubcontractor, selectedJobsite, selectedWorker]);

  // Reset filters
  const handleResetFilters = useCallback(() => {
    setSelectedSubcontractor('all');
    setSelectedJobsite('all');
    setSelectedWorker('all');
    setReportInterval('weekly');
  }, []);

  // Determine entity type and ID for ComplianceReportsViewer
  const getReportConfig = useCallback(() => {
    if (selectedWorker !== 'all') {
      return {
        entityType: 'worker',
        entityId: selectedWorker,
      };
    } else if (selectedSubcontractor !== 'all') {
      return {
        entityType: 'company',
        entityId: selectedSubcontractor,
      };
    } else {
      return {
        entityType: 'company',
        entityId: user?.organizationId || user?.organization,
      };
    }
  }, [selectedWorker, selectedSubcontractor, user?.organizationId, user?.organization]);

  const reportConfig = getReportConfig();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <FiRefreshCw className="w-6 h-6 animate-spin text-yellow-500" />
          <span className="text-white">Loading reports...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Compliance Reports</h1>
            <p className="text-gray-400">
              Filter and export comprehensive compliance data across your organization
            </p>
          </div>

          <div className="mt-4 md:mt-0 flex items-center space-x-3">
            <button
              onClick={handleExportPDF}
              className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              <FiDownload className="w-4 h-4" />
              <span>Export PDF</span>
            </button>
            <button
              onClick={handleExportCSV}
              className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              <FiDownload className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <FiFilter className="w-5 h-5 text-yellow-500" />
              <h2 className="text-xl font-semibold text-white">Filters</h2>
            </div>
            <button
              onClick={handleResetFilters}
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              Reset All
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Subcontractor Filter (GC only) */}
            {user.role === 'gcAdmin' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <FiBriefcase className="inline w-4 h-4 mr-1" />
                  Subcontractor
                </label>
                <select
                  value={selectedSubcontractor}
                  onChange={(e) => setSelectedSubcontractor(e.target.value)}
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="all">All Subcontractors</option>
                  {subcontractors.map((sc) => (
                    <option key={sc._id || sc.id} value={sc._id || sc.id}>
                      {sc.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Jobsite Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <FiMapPin className="inline w-4 h-4 mr-1" />
                Jobsite
              </label>
              <select
                value={selectedJobsite}
                onChange={(e) => setSelectedJobsite(e.target.value)}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="all">All Jobsites</option>
                {jobsites.map((jobsite) => (
                  <option key={jobsite._id || jobsite.id} value={jobsite._id || jobsite.id}>
                    {jobsite.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Worker Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <FiUsers className="inline w-4 h-4 mr-1" />
                Worker
              </label>
              <select
                value={selectedWorker}
                onChange={(e) => setSelectedWorker(e.target.value)}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="all">All Workers</option>
                {workers.map((worker) => (
                  <option key={worker._id || worker.id} value={worker._id || worker.id}>
                    {worker.firstName} {worker.lastName} ({worker.email})
                  </option>
                ))}
              </select>
            </div>

            {/* Report Interval Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Report Interval
              </label>
              <select
                value={reportInterval}
                onChange={(e) => setReportInterval(e.target.value)}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="semiannual">Semi-Annual</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
          </div>

          {/* Active Filters Summary */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-400">Active Filters:</span>
            {selectedSubcontractor !== 'all' && (
              <span className="bg-blue-600 text-white text-xs px-3 py-1 rounded-full">
                SC:{' '}
                {subcontractors.find((sc) => (sc._id || sc.id) === selectedSubcontractor)?.name ||
                  'Unknown'}
              </span>
            )}
            {selectedJobsite !== 'all' && (
              <span className="bg-green-600 text-white text-xs px-3 py-1 rounded-full">
                Jobsite:{' '}
                {jobsites.find((j) => (j._id || j.id) === selectedJobsite)?.name || 'Unknown'}
              </span>
            )}
            {selectedWorker !== 'all' && (
              <span className="bg-purple-600 text-white text-xs px-3 py-1 rounded-full">
                Worker:{' '}
                {workers.find((w) => (w._id || w.id) === selectedWorker)?.firstName || 'Unknown'}
              </span>
            )}
            {selectedSubcontractor === 'all' &&
              selectedJobsite === 'all' &&
              selectedWorker === 'all' && (
                <span className="text-gray-500 text-sm">No filters applied (showing all data)</span>
              )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-900 border border-red-700 rounded-lg p-4 text-red-200">
            Error: {error}
          </div>
        )}

        {/* Reports Viewer */}
        <div className="bg-gray-800 rounded-lg p-6">
          <ComplianceReportsViewer
            entityType={reportConfig.entityType}
            entityId={reportConfig.entityId}
            defaultInterval={reportInterval}
            showWorkerDetails={selectedWorker === 'all'}
          />
        </div>
      </div>
    </div>
  );
}
